import { mediaFramesFromSource } from 'creation'
import { getResults } from 'shared/api'
import { POST } from 'shared/util/fetch-methods'
import { FPS } from '../constants'
import { CreationToolFrame, CreationToolSourceFile } from '../types'
import { preloadImage } from './preload'

const MAX_FILE_SIZE = 5 // mb
const DOWNSAMPLE_STEP = 0.8

const getCurrentFileSize = (urls: string[]): number => {
    let fileSize = 0

    urls.forEach((url) => {
        const bytes = url.length * 1.37
        fileSize += bytes / (1024 * 1024)
    })

    return fileSize
}

const decreaseFileSize = async (urls: string[]): Promise<string[]> => {
    const promises: Promise<string>[] = urls.map(
        (url) =>
            new Promise((resolve, reject) => {
                const img = document.createElement('img')
                const canvas = document.createElement('canvas')
                const context = canvas.getContext('2d')
                img.onload = () => {
                    canvas.width = Math.round(img.naturalWidth * DOWNSAMPLE_STEP)
                    canvas.height = Math.round(img.naturalHeight * DOWNSAMPLE_STEP)
                    context?.drawImage(img, 0, 0, canvas.width, canvas.height)
                    resolve(canvas.toDataURL('image/jpeg').split(';base64,')[1])
                }
                img.onerror = reject
                img.src = `data:image/jpeg;base64,${url}`
            })
    )

    return Promise.all(promises)
}

const applyMask = async (frame: CreationToolFrame, mask: string): Promise<CreationToolFrame> => {
    const img = await preloadImage(`data:image/jpeg;base64,${mask}`)
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d', { willReadFrequently: true })
    const frameContext = frame.canvas.getContext('2d', { willReadFrequently: true })

    canvas.width = frame.canvas.width
    canvas.height = frame.canvas.height

    if (context && frameContext) {
        context.drawImage(img, 0, 0, canvas.width, canvas.height)

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
        const frameImageData = frameContext.getImageData(0, 0, canvas.width, canvas.height)

        for (let i = 0; i < imageData.data.length; i += 4) {
            const r = imageData.data[i]
            const g = imageData.data[i + 1]
            const b = imageData.data[i + 2]
            const average = (r + g + b) / 3

            imageData.data[i] = frameImageData.data[i]
            imageData.data[i + 1] = frameImageData.data[i + 1]
            imageData.data[i + 2] = frameImageData.data[i + 2]
            imageData.data[i + 3] = Math.round(average / 255) * 255
        }

        context.putImageData(imageData, 0, 0)
    }

    return { ...frame, canvas }
}

export const getStickerFromSource = async (
    sourceFile: CreationToolSourceFile,
    version?: string
): Promise<CreationToolSourceFile> => {
    const mediaFrames = await mediaFramesFromSource(sourceFile.source, undefined, true)
    const creationFrames: CreationToolFrame[] = mediaFrames.map((frame) => ({
        canvas: frame.canvas,
        frameCount: Math.max(1, Math.round(frame.delay * FPS)),
    }))
    const maskedFrames = await getStickerFromCanvas(creationFrames, version)

    return {
        ...sourceFile,
        frames: maskedFrames,
        hasAudio: false,
        isTransparent: true,
        preview: maskedFrames[0].canvas,
    }
}

export const getStickerFromCanvas = async (
    canvases: CreationToolFrame[],
    version?: string
): Promise<CreationToolFrame[]> => {
    let encodedFrames = canvases.map(({ canvas }) => canvas.toDataURL('image/jpeg').split(';base64,')[1])
    let currentFileSize = getCurrentFileSize(encodedFrames)

    // downsample to fit max file size
    while (currentFileSize > MAX_FILE_SIZE) {
        encodedFrames = await decreaseFileSize(encodedFrames)
        currentFileSize = getCurrentFileSize(encodedFrames)
    }

    const params: {
        frames: string[]
        version?: string
    } = { frames: encodedFrames }

    if (version) {
        params.version = version
    }

    const {
        data: { frames = [] },
    } = await getResults('/ajax/gif/remove_background', POST(params, true))

    const maskedFrames: CreationToolFrame[] = []

    for (let i = 0; i < frames.length; i++) {
        const masked = await applyMask(canvases[i], frames[i].mask)
        maskedFrames.push(masked)
    }

    return maskedFrames
}

export default getStickerFromSource
